import { NoteText } from '@/components/mentions/NoteText';
import { NoteComposer } from '@/components/mentions/NoteComposer';
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import emojiRegex from 'emoji-regex';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useDispatch } from 'react-redux';
import {
	updateReactionOnTeamMessage,
	editTeamMessage,
	deleteTeamMessage,
	setTeamReply,
	pinMessageToNotes,
} from '../../../actions/teams';
import { saveTeamPhotoToCustomer } from '../../../actions/customerGallery';
import ReplyQuote from './ReplyQuote';
import CustomerCard from './CustomerCard';
import { withoutCustomerToken } from './customerLink';
import AttachmentGallery from './AttachmentGallery';
import Reactions from './Reactions';
import UserAvatar from './UserAvatar';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/components/ui/theme-provider';
import {
	Copy,
	ImagePlus,
	MoreHorizontal,
	Pencil,
	Pin,
	Reply,
	Smile,
	SmilePlus,
	Trash2,
	Video,
} from 'lucide-react';
import linkifyHtml from 'linkify-html';
import DeleteMessageDialog from './DeleteMessageDialog';
import moment from 'moment';
import { useIsMobile, isTouchViewport } from '@/hooks/use-mobile';
import {
	ActionSheet,
	BottomSheet,
	BottomSheetTextButton,
	useLongPress,
} from '@/components/mobile';

// The reactions offered first on a phone, as in iMessage and Slack.
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const getUrlFromMessage = (message) => {
	const urlRegex = /(https?:\/\/[^\s]+)/;

	// Use the match method to extract the URL
	const urlMatch = message.match(urlRegex);

	// Check if a URL is found
	if (urlMatch) {
		const url = urlMatch[0]; // Extracted URL
		return url;
	}

	return null;
};

// Images uploaded to Teams can be saved to a customer. GIFs from Giphy are not stored.
const isSavablePhoto = (attachment) =>
	attachment?.type?.startsWith('image/') && !attachment.url?.includes('giphy.com');

// Function to check if a string contains only emojis
function containsOnlyEmojis(text) {
	const regex = emojiRegex();
	const emojis = text.match(regex) || [];

	// Use the `join('')` method to concatenate the emojis into a single string
	const concatenatedEmojis = emojis.join('');

	// Use the `===` operator to check if the concatenated emojis equal the original text
	return concatenatedEmojis === text;
}

const Text = ({ text, mentions, team, activeUsersInConversation, _mentionedUsers = [] }) => {
  if (mentions) return <NoteText text={text} mentions={mentions} team={team} />;
	const names = activeUsersInConversation
		.filter((user) => _mentionedUsers.includes(user._id))
		.map((user) => user.name);

	// Helper function to escape HTML entities
	const escapeHtml = (text) => {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	};

	const formatText = (inputText) => {
		try {
			// First escape HTML entities to prevent HTML injection
			const escapedText = escapeHtml(inputText);

			// Use linkifyHtml to convert URLs to <a> tags
			let formattedText = linkifyHtml(escapedText, {
				target: '_blank',
				rel: 'noopener noreferrer',
				className: 'linkified text-blue-500',
			});

			// Convert @ mentions to bold if they are in the names array
			names.forEach((name) => {
				const escapedName = name.replace(
					/[-/\\^$*+?.()|[\]{}]/g,
					'\\$&',
				); // Escape special regex characters
				const mentionRegex = new RegExp(`@${escapedName}`, 'g');
				formattedText = formattedText.replace(
					mentionRegex,
					`<strong>@${name}</strong>`,
				);
			});

			return { __html: formattedText };
		} catch (error) {
			console.warn('linkifyHtml error:', error);
			// Fallback: Simple URL detection and replacement
			// First escape HTML entities
			const escapedText = escapeHtml(inputText);
			const urlRegex = /(https?:\/\/[^\s]+)/g;
			let formattedText = escapedText.replace(urlRegex, (url) => {
				return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="linkified text-blue-500">${url}</a>`;
			});

			// Convert @ mentions to bold if they are in the names array
			names.forEach((name) => {
				const escapedName = name.replace(
					/[-/\\^$*+?.()|[\]{}]/g,
					'\\$&',
				); // Escape special regex characters
				const mentionRegex = new RegExp(`@${escapedName}`, 'g');
				formattedText = formattedText.replace(
					mentionRegex,
					`<strong>@${name}</strong>`,
				);
			});

			return { __html: formattedText };
		}
	};

	return (
		<div
			dangerouslySetInnerHTML={formatText(text)}
			style={{
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
				overflowWrap: 'break-word',
			}}
		/>
	);
};

const ChatBubble = ({
	message,
  _customer, _business,
	isAuthenticatedUser,
	event,
	currentTime,
	name,
	dateLabel,
	isDirectMessage,
	user,
	reactions,
	_authenticatedUser,
	showMessageActionsForActiveUsersInConversation,
	activeUsersInConversation,
	showAvatar = true,
	isRunner,
	// runs of messages from one person: the first shows their name, the last
	// their avatar and the time
	isFirstInGroup = true,
	isLastInGroup = true,
}) => {
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const [isEditing, setIsEditing] = useState(null);
	const [editedMessage, setEditedMessage] = useState(message.text);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
	const { resolvedTheme: theme } = useTheme();
	const textareaRef = useRef(null);
	const saveButtonRef = useRef(null);

	// Phones have no hover: a tap or a long press on the message opens its
	// reactions and actions in a sheet. Tablets keep the hover toolbar, and a
	// long press opens its menu there.
	const isMobile = useIsMobile();
	const isTouch = isMobile || isTouchViewport();
	const [phoneActionsOpen, setPhoneActionsOpen] = useState(false);
	const [phoneEmojiOpen, setPhoneEmojiOpen] = useState(false);
	// mount the sheets on first use, not once per message in the thread
	const [phoneSheetsMounted, setPhoneSheetsMounted] = useState(false);
	// tablets: "More reactions" in the menu opens the picker once the menu has closed
	const openPickerAfterMenuRef = useRef(false);
	const canLongPress =
		isTouch &&
		Boolean(message) &&
		!event &&
		showMessageActionsForActiveUsersInConversation &&
		!message.isDeleted;
	// useLongPress drops the click that follows the finger lifting, so the
	// sheet that opens under it isn't tapped by accident.
	const longPress = useLongPress(openTouchActions, { disabled: !canLongPress });

	if (!message) return null;

	const isVideoChatMessage =
		message?.text?.includes('http://localhost:3002') ||
		message?.text?.includes('https://video.chekkit.io') ||
		message?.text?.includes('https://www.videowith.me');

	const dispatch = useDispatch();

	const createdAtMoment = moment(message.createdAt);
	const formattedCreatedAt = createdAtMoment.format('ll, LT');
	const formattedMessageTime = currentTime || createdAtMoment.format('LT');

	// adds a reaction to the current {message}
	const addOrUpdateEmoji = async (emoji) => {
		const reaction = { _user: _authenticatedUser, emoji: emoji.native };
		const existingReaction = message.reactions.find(
			(r) => r._user === _authenticatedUser && r.emoji === emoji.native,
		);

		if (existingReaction) {
			// User has already reacted with the same emoji, so return without adding the reaction again

			return;
		}

		// User hasn't reacted yet, add a new reaction
		const updatedReactions = [...message.reactions, reaction];
		const updatedTeamMessage = { ...message, reactions: updatedReactions };

		// Update the message with the updated reactions
		dispatch(
			updateReactionOnTeamMessage({
				message,
				updatedTeamMessage,
				emoji: emoji.native,
				isReactionAddedToMessage: true,
			}),
		);

		setShowEmojiPicker(false);
	};

	// removes a reaction from the current {message}
	const handleReactionClick = (emoji) => {
		// Check if the authenticated user exists in any of the reactions
		if (
			!message.reactions.find(
				(reaction) => reaction._user === _authenticatedUser,
			)
		) {
			// User has not reacted to this message, no need to proceed
			return null;
		}

		// remove reaction
		const updatedReactions = message.reactions.filter(
			(reaction) =>
				!(
					reaction.emoji === emoji &&
					reaction._user === _authenticatedUser
				),
		);

		const updatedTeamMessage = {
			...message,
			reactions: updatedReactions,
		};

		dispatch(
			updateReactionOnTeamMessage({
				message,
				updatedTeamMessage,
				emoji,
				isReactionAddedToMessage: false,
			}),
		);
	};

	const handleEditMessage = (messageId) => {
		setIsEditing(messageId);
	};

	// Auto-resize textarea based on content
	const autoResizeTextarea = (textarea) => {
		if (textarea) {
			textarea.style.height = 'auto';
			textarea.style.height = Math.max(textarea.scrollHeight, 80) + 'px';
		}
	};

	// Set up auto-resize when editing starts and scroll Save button into view
	useEffect(() => {
		if (isEditing === message._id && textareaRef.current) {
			// Initial resize
			autoResizeTextarea(textareaRef.current);
			// Focus the textarea
			textareaRef.current.focus();

			// Scroll the Save button into view with a small delay to ensure DOM is updated
			setTimeout(() => {
				if (saveButtonRef.current) {
					saveButtonRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'end',
						inline: 'nearest',
					});
				}
			}, 100);
		}
	}, [isEditing, message._id]);

	const showDeleteConfirmationDialog = () => {
		// show delete confirmation dialog
		setShowDeleteDialog(true);
	};

	const handleDeleteMessage = () => {
		// delete message on the backend
		dispatch(deleteTeamMessage({ teamMessage: message }));
	};

	const saveMessage = (newMessage) => {
		const updatedTeamMessage = { ...message, text: newMessage };

		// update message on the backend
		dispatch(
			editTeamMessage({
				updatedTeamMessage,
			}),
		);
	};

	// Replies and pins are for team chat, not customer notes
	const canReply = !isRunner && !_customer;
	const canPin = canReply && !isDirectMessage && !!message.text?.trim();
	const photos = (message.attachments || []).filter(isSavablePhoto);
	const photoCustomerName = message._attachedCustomer?.name || 'the customer';
	const canSavePhotos = photos.length > 0 && !!(message._attachedCustomer || _customer);
	const canEditOrDelete = isRunner || isAuthenticatedUser;

	const handleReply = () => {
		setIsActionMenuOpen(false);
		dispatch(
			setTeamReply({
				_teamConversation: message._teamConversation,
				message,
			})
		);
		document.getElementById('teamTextBox')?.focus();
	};

	const handlePinToNotes = async () => {
		setIsActionMenuOpen(false);
		const result = await dispatch(pinMessageToNotes({ teamMessage: message }));

		if (result?.error) {
			toast.error(result.message || 'Could not pin this message.');
			return;
		}

		toast.success('Pinned to notes.');
	};

	const handleSavePhotos = async () => {
		setIsActionMenuOpen(false);
		const results = await Promise.all(
			photos.map((attachment) =>
				saveTeamPhotoToCustomer({ teamMessage: message, attachment })
			)
		);
		const failed = results.find((result) => result.error);

		if (failed) {
			toast.error(failed.message || 'Could not save the photo.');
			return;
		}

		toast.success(
			results.every((result) => result.alreadySaved)
				? `Already saved to ${photoCustomerName}.`
				: `Saved to ${photoCustomerName}'s team photos.`
		);
	};

	const handleOpenVideoChat = () => {
		const url = getUrlFromMessage(message.text);
		if (url) {
			window.open(url, '_blank'); // Open URL in a new tab
		}
	};

	const isOwn = isAuthenticatedUser;
	const isEditingThis = isEditing === message._id;
	const linkedCustomer = !message.isDeleted ? message._attachedCustomer : null;
	// hide the old "[Name]" token, unless that would move an @mention
	const customerToken = linkedCustomer?.name ? `[${linkedCustomer.name}]` : null;
	const tokenAt = customerToken ? (message.text || '').indexOf(customerToken) : -1;
	const displayText =
		tokenAt >= 0 && !(message.mentions || []).some((m) => m.start > tokenAt)
			? withoutCustomerToken(message.text, linkedCustomer)
			: message.text;
	const isEmojiOnly = !!displayText && containsOnlyEmojis(displayText);
	const hasAttachments = message?.attachments?.length > 0;
	const canAct =
		!event && !isVideoChatMessage && showMessageActionsForActiveUsersInConversation;
	const hasMenu = canAct && (canReply || canPin || canSavePhotos || canEditOrDelete);
	const toolbarOpen = isActionMenuOpen || showEmojiPicker;
	// notes show who wrote them and when on every note
	const showNameAbove =
		!event && (isRunner || (!isOwn && !isDirectMessage && isFirstInGroup && name));

	const bubbleClass = cn(
		'whitespace-pre-wrap break-words rounded-[18px] px-3.5 py-2 text-[15px] leading-relaxed text-foreground',
		isOwn ? 'bg-bubble-team' : 'border border-border/70 bg-muted/65',
		isLastInGroup && (isOwn ? 'rounded-br-[5px]' : 'rounded-bl-[5px]'),
		message.isSending && 'opacity-60'
	);

	const renderEditor = () => {
		if (_customer || isDirectMessage === false) {
			return (
				<NoteComposer
					_teamConversation={!_customer ? message._teamConversation : undefined}
					draftKey={`edit:${message._id}`}
					_customer={_customer}
					_business={_business}
					initialText={message.text}
					initialMentions={message.mentions || []}
					expectedRevision={message.noteRevision || 0}
					onCancel={() => setIsEditing(null)}
					onSave={async (draft) => {
						const result = await dispatch(
							editTeamMessage({ updatedTeamMessage: { ...message, ...draft } })
						);
						if (!result?.error) setIsEditing(null);
						return result;
					}}
				/>
			);
		}

		return (
			<div className="flex w-full flex-col rounded-2xl border border-foreground/30 bg-background p-1 focus-within:border-foreground/60 focus-within:ring-1 focus-within:ring-ring/30">
				<Textarea
					ref={textareaRef}
					id={`textarea-${message._id}`}
					defaultValue={message?.text}
					onChange={(e) => {
						setEditedMessage(e.target.value);
						autoResizeTextarea(e.target);
					}}
					enterKeyHint={isMobile ? 'enter' : undefined}
					onKeyPress={(e) => {
						if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
							e.preventDefault();
							saveMessage(e.target.value);
							setIsEditing(null);
						}
					}}
					className="min-h-[80px] w-full resize-none border-0 bg-transparent px-2.5 py-2 text-[15px] leading-6 shadow-none focus-visible:ring-0 max-md:text-base"
					style={{ boxSizing: 'border-box', overflow: 'hidden' }}
				/>
				<div className="flex justify-end gap-1 p-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setIsEditing(null)}
						className="rounded-lg"
					>
						Cancel
					</Button>
					<Button
						ref={saveButtonRef}
						size="sm"
						className="rounded-lg"
						disabled={editedMessage?.trim() === '' || editedMessage === message?.text}
						onClick={() => {
							saveMessage(textareaRef.current?.value || editedMessage);
							setIsEditing(null);
						}}
					>
						Save
					</Button>
				</div>
			</div>
		);
	};

	const renderVideoChat = () => (
		<button
			type="button"
			onClick={handleOpenVideoChat}
			className="flex items-center gap-3 rounded-2xl border border-border/70 bg-background px-3 py-2.5 text-left shadow-[0_1px_2px_rgba(0,0,0,.04)] transition-colors hover:bg-stone-50 dark:hover:bg-stone-900"
		>
			<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
				<Video className="size-4" />
			</span>
			<span className="flex flex-col">
				<span className="text-sm font-medium">Video call</span>
				<span className="text-xs text-muted-foreground">Click to join</span>
			</span>
		</button>
	);

	const renderText = () => {
		if (isEmojiOnly) {
			return (
				<p className="px-1 text-4xl leading-tight" {...phoneBubbleProps}>
					{displayText}
				</p>
			);
		}

		return (
			<div className={bubbleClass} {...phoneBubbleProps}>
				<Text
					team={!_customer && isDirectMessage === false}
					text={displayText}
					mentions={message.mentions}
					_mentionedUsers={message._mentionedUsers}
					activeUsersInConversation={activeUsersInConversation}
				/>
			</div>
		);
	};

	const dateSeparator = dateLabel ? (
		isRunner ? (
			<div className="my-4 h-px w-full bg-border/60" />
		) : (
			<div className="my-4 flex justify-center">
				<span className="rounded-full bg-muted/70 px-3 py-1 text-[11px] font-medium text-muted-foreground">
					{dateLabel}
				</span>
			</div>
		)
	) : null;

	if (event) {
		return (
			<div style={{ display: message.isDeleted ? 'none' : '' }}>
				{dateSeparator}
				<div className="my-2.5 flex justify-center px-6">
					<p className="max-w-[85%] break-words text-center text-xs leading-5 text-muted-foreground">
						{message.text}
					</p>
				</div>
			</div>
		);
	}

	const copyText = async () => {
		try {
			await navigator.clipboard.writeText(displayText || '');
			toast.success('Copied', { position: 'top-center', duration: 1500 });
		} catch {
			toast.error('Could not copy this message', { position: 'top-center' });
		}
	};

	const openPhoneActions = () => {
		setPhoneSheetsMounted(true);
		setPhoneActionsOpen(true);
	};

	// long press: the phone sheet, or the hover toolbar's menu on a tablet
	function openTouchActions() {
		if (!canAct || isEditingThis) return;
		if (isMobile) {
			openPhoneActions();
		} else if (hasMenu) {
			setIsActionMenuOpen(true);
		} else {
			setShowEmojiPicker(true);
		}
	}

	const phoneBubbleProps =
		isMobile && canAct && !isEditingThis
			? {
					role: 'button',
					tabIndex: 0,
					'aria-haspopup': 'dialog',
					onClick: (clickEvent) => {
						if (clickEvent.target.closest?.('a, button, input, textarea')) return;
						openPhoneActions();
					},
					onKeyDown: (keyEvent) => {
						if (keyEvent.target !== keyEvent.currentTarget) return;
						if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
							keyEvent.preventDefault();
							openPhoneActions();
						}
					},
				}
			: {};

	const toggleQuickReaction = (emoji) => {
		const mine = (message.reactions || []).some(
			(r) => r._user === _authenticatedUser && r.emoji === emoji
		);
		if (mine) {
			handleReactionClick(emoji);
		} else {
			addOrUpdateEmoji({ native: emoji });
		}
		setPhoneActionsOpen(false);
	};

	const renderPhoneSheets = () => {
		if (!isMobile || !phoneSheetsMounted) return null;

		const phoneItems = [
			canReply && {
				id: 'reply',
				label: 'Reply',
				icon: Reply,
				onSelect: () => {
					handleReply();
					// the sheet's focus trap is still up during onSelect
					setTimeout(() => document.getElementById('teamTextBox')?.focus(), 0);
				},
			},
			displayText &&
				!isVideoChatMessage && {
					id: 'copy',
					label: 'Copy text',
					icon: Copy,
					onSelect: copyText,
				},
			canPin && { id: 'pin', label: 'Pin to notes', icon: Pin, onSelect: handlePinToNotes },
			canSavePhotos && {
				id: 'save-photos',
				label: `${photos.length > 1 ? 'Save photos' : 'Save photo'} to ${photoCustomerName}`,
				icon: ImagePlus,
				onSelect: handleSavePhotos,
			},
			canEditOrDelete &&
				!hasAttachments &&
				message.text && {
					id: 'edit',
					label: 'Edit message',
					icon: Pencil,
					onSelect: () => handleEditMessage(message._id),
				},
			canEditOrDelete && {
				id: 'delete',
				label: 'Delete message',
				icon: Trash2,
				destructive: true,
				page: 'delete',
			},
		].filter(Boolean);
		const sender = isOwn ? 'You' : name || user?.name;

		return (
			<>
				<ActionSheet
					open={phoneActionsOpen}
					onOpenChange={setPhoneActionsOpen}
					title={[sender, formattedCreatedAt].filter(Boolean).join(' · ')}
					header={
						<div
							role="group"
							aria-label="Reactions"
							className="mb-2 flex items-center gap-0.5 rounded-[14px] bg-background p-1 dark:bg-zinc-900"
						>
							{QUICK_REACTIONS.map((emoji) => {
								const mine = (message.reactions || []).some(
									(r) => r._user === _authenticatedUser && r.emoji === emoji
								);
								return (
									<button
										key={emoji}
										type="button"
										aria-label={mine ? `Remove ${emoji} reaction` : `React with ${emoji}`}
										aria-pressed={mine}
										onClick={() => toggleQuickReaction(emoji)}
										className={cn(
											'flex h-11 min-w-11 flex-1 items-center justify-center rounded-full text-[24px] leading-none transition-transform active:scale-90 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
											mine && 'bg-primary/15 ring-1 ring-inset ring-primary/40',
											// the narrowest phones fit six 44px buttons, so the last quick reaction moves to More
											emoji === QUICK_REACTIONS[QUICK_REACTIONS.length - 1] && 'max-[359px]:hidden'
										)}
									>
										{emoji}
									</button>
								);
							})}
							<button
								type="button"
								aria-label="More reactions"
								onClick={() => {
									setPhoneActionsOpen(false);
									setPhoneEmojiOpen(true);
								}}
								className="flex h-11 min-w-11 flex-1 items-center justify-center rounded-full text-muted-foreground active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							>
								<SmilePlus className="size-6" aria-hidden="true" />
							</button>
						</div>
					}
					items={phoneItems}
					pages={[
						{
							id: 'delete',
							title: 'Delete message?',
							content: (
								<p className="px-3 pb-3 text-center text-[13px] leading-snug text-muted-foreground">
									Are you sure you want to delete this message?
								</p>
							),
							sections: [
								{
									items: [
										{
											id: 'confirm-delete',
											label: 'Delete message',
											icon: Trash2,
											destructive: true,
											onSelect: handleDeleteMessage,
										},
									],
								},
							],
						},
					]}
				/>
				<BottomSheet
					open={phoneEmojiOpen}
					onOpenChange={setPhoneEmojiOpen}
					title="Add a reaction"
					size="large"
					headerLeft={
						<BottomSheetTextButton onClick={() => setPhoneEmojiOpen(false)}>
							Cancel
						</BottomSheetTextButton>
					}
					bodyClassName="px-0 pt-0 [&_em-emoji-picker]:h-full [&_em-emoji-picker]:w-full"
				>
					<Picker
						onEmojiSelect={(emoji) => {
							addOrUpdateEmoji(emoji);
							setPhoneEmojiOpen(false);
						}}
						dynamicWidth
						previewPosition="none"
						skinTonePosition="none"
						navPosition="bottom"
						data={data}
						theme={theme}
					/>
				</BottomSheet>
			</>
		);
	};

	const iconButtonClass =
		'flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring touch:size-11';
	const touchMenuItemClass = 'touch:min-h-11';
	// Touch screens hide the reaction button (it is in the "…" menu), so the
	// picker opens beside the toolbar instead.
	const withTouchAnchor = (toolbar) =>
		isTouch && hasMenu ? <PopoverAnchor asChild>{toolbar}</PopoverAnchor> : toolbar;

	return (
		<div
			className={cn(isFirstInGroup ? 'mt-3' : 'mt-0.5')}
			style={{ display: message.isDeleted ? 'none' : '' }}
		>
			{dateSeparator}
			<div
				className={cn(
					'group/message flex w-full items-end gap-2 px-2',
					isOwn ? 'flex-row-reverse' : 'flex-row'
				)}
			>
				{!isOwn && showAvatar && (
					<div className="w-8 shrink-0">
						{isLastInGroup && user && (
							<UserAvatar
								user={user}
								showName={false}
								showNameBelow={false}
								avatarWidth="32px"
								avatarHeight="32px"
								avatarFontSize="12px"
								onlineIndicatorHeight="0.5rem"
								onlineIndicatorWidth="0.5rem"
							/>
						)}
					</div>
				)}

				<div
					className={cn(
						'flex min-w-0 flex-col',
						isOwn ? 'items-end' : 'items-start',
						isEditingThis ? 'w-full max-w-full' : 'max-w-[75%]',
						canLongPress && !isEditingThis && 'touch:select-none touch:[-webkit-touch-callout:none]'
					)}
					{...(canLongPress && !isEditingThis ? longPress : {})}
				>
					{showNameAbove && (
						<span className="mb-1 px-1 text-xs text-muted-foreground">
							{isRunner ? `${name ? `${name} · ` : ''}${formattedCreatedAt}` : name}
						</span>
					)}

					{message.replyTo && !message.isDeleted && (
						<ReplyQuote message={message} isAuthenticatedUser={isOwn} />
					)}

					<div
						className={cn(
							'relative flex max-w-full flex-col gap-1',
							isOwn ? 'items-end' : 'items-start',
							isEditingThis && 'w-full'
						)}
					>
						{hasAttachments && (
							<AttachmentGallery
								key={message._id}
								attachments={message.attachments}
								isSentByAuthUser={isOwn}
								isLastMessageBySameUser={name}
								message={message}
							/>
						)}

						{isEditingThis
							? message.text && renderEditor()
							: displayText &&
								(isVideoChatMessage ? renderVideoChat() : renderText())}

						{linkedCustomer && !isEditingThis && (
							<CustomerCard customer={linkedCustomer} />
						)}

						{/* phones: messages without a text bubble (photos, a linked
						    customer) get a visible way into their actions */}
						{isMobile && canAct && !isEditingThis && !displayText && (
							<button
								type="button"
								aria-label="Message actions"
								aria-haspopup="dialog"
								onClick={openPhoneActions}
								className={cn(
									'absolute top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
									isOwn ? 'right-full' : 'left-full'
								)}
							>
								<span className="flex size-7 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground shadow-sm">
									<MoreHorizontal className="size-4" aria-hidden="true" />
								</span>
							</button>
						)}

						{/* reactions and actions, beside the message on hover. Touch
						    screens from 768px have no hover: they show the "…" (whose
						    menu also has the reactions), and a long press opens it. */}
						{canAct && !isEditingThis && !isMobile && (
							<Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
								{withTouchAnchor(
									<div
										className={cn(
											'absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded-full border border-border/60 bg-background p-0.5 shadow-sm transition-opacity',
											isOwn ? 'right-full mr-1.5' : 'left-full ml-1.5',
											toolbarOpen
												? 'opacity-100'
												: 'opacity-0 focus-within:opacity-100 group-hover/message:opacity-100 touch:opacity-100',
											hasMenu && 'touch:border-transparent touch:bg-transparent touch:shadow-none'
										)}
									>
										<PopoverTrigger asChild>
											<button
												type="button"
												aria-label="Add reaction"
												className={cn(iconButtonClass, hasMenu && 'touch:hidden')}
											>
												<Smile className="size-4" />
											</button>
										</PopoverTrigger>

										{hasMenu && (
											<DropdownMenu
												open={isActionMenuOpen}
												onOpenChange={setIsActionMenuOpen}
											>
												<DropdownMenuTrigger asChild>
													<button
														type="button"
														aria-label="Message actions"
														className={iconButtonClass}
													>
														<MoreHorizontal className="size-4" />
													</button>
												</DropdownMenuTrigger>
												<DropdownMenuContent
													align={isOwn ? 'end' : 'start'}
													onCloseAutoFocus={(closeEvent) => {
														if (!openPickerAfterMenuRef.current) return;
														// open the picker after the menu, without focus going
														// back to "…" (which would close the picker again)
														openPickerAfterMenuRef.current = false;
														closeEvent.preventDefault();
														setShowEmojiPicker(true);
													}}
												>
													{isTouch && (
														<>
															<div
																role="group"
																aria-label="Reactions"
																className="flex items-center gap-0.5 pb-1"
															>
																{QUICK_REACTIONS.map((emoji) => {
																	const mine = (message.reactions || []).some(
																		(r) => r._user === _authenticatedUser && r.emoji === emoji
																	);
																	return (
																		<DropdownMenuItem
																			key={emoji}
																			aria-label={mine ? `Remove ${emoji} reaction` : `React with ${emoji}`}
																			onSelect={() => toggleQuickReaction(emoji)}
																			className={cn(
																				'size-11 justify-center rounded-full p-0 text-[22px] leading-none',
																				mine && 'bg-primary/15'
																			)}
																		>
																			{emoji}
																		</DropdownMenuItem>
																	);
																})}
															</div>
															<DropdownMenuItem
																className={touchMenuItemClass}
																onSelect={() => {
																	openPickerAfterMenuRef.current = true;
																}}
															>
																<SmilePlus className="mr-2 size-4" />
																More reactions
															</DropdownMenuItem>
															<DropdownMenuSeparator />
														</>
													)}
													{canReply && (
														<DropdownMenuItem className={touchMenuItemClass} onClick={handleReply}>
															<Reply className="mr-2 size-4" />
															Reply
														</DropdownMenuItem>
													)}
													{canPin && (
														<DropdownMenuItem className={touchMenuItemClass} onClick={handlePinToNotes}>
															<Pin className="mr-2 size-4" />
															Pin to notes
														</DropdownMenuItem>
													)}
													{canSavePhotos && (
														<DropdownMenuItem className={touchMenuItemClass} onClick={handleSavePhotos}>
															<ImagePlus className="mr-2 size-4" />
															{photos.length > 1 ? 'Save photos' : 'Save photo'} to{' '}
															{photoCustomerName}
														</DropdownMenuItem>
													)}
													{canEditOrDelete && !hasAttachments && message.text && (
														<DropdownMenuItem
															className={touchMenuItemClass}
															onClick={() => {
																setIsActionMenuOpen(false);
																handleEditMessage(message._id);
															}}
														>
															<Pencil className="mr-2 size-4" />
															Edit message
														</DropdownMenuItem>
													)}
													{canEditOrDelete && (
														<DropdownMenuItem
															className={cn('text-destructive focus:text-destructive', touchMenuItemClass)}
															onClick={() => {
																setIsActionMenuOpen(false);
																showDeleteConfirmationDialog();
															}}
														>
															<Trash2 className="mr-2 size-4" />
															Delete message
														</DropdownMenuItem>
													)}
												</DropdownMenuContent>
											</DropdownMenu>
										)}
									</div>
								)}
								<PopoverContent className="m-0 w-full border-0 bg-transparent p-0 outline-0">
									<Picker
										onEmojiSelect={addOrUpdateEmoji}
										showPreview={false}
										showSkinTones={false}
										useButton={false}
										data={data}
										theme={theme}
									/>
								</PopoverContent>
							</Popover>
						)}
					</div>

					{reactions?.length > 0 && (
						<Reactions
							reactions={reactions}
							isAuthenticatedUser={isOwn}
							_authenticatedUser={_authenticatedUser}
							onReactionClick={handleReactionClick}
						/>
					)}

					{isLastInGroup && !isRunner && !isEditingThis && (
						<span className="mt-1 px-1 text-[11px] leading-4 text-muted-foreground">
							{message.isSending ? 'Sending…' : formattedMessageTime}
							{message.isEdited && ' · Edited'}
						</span>
					)}
					{isRunner && message.isEdited && !isEditingThis && (
						<span className="mt-1 px-1 text-[11px] leading-4 text-muted-foreground">
							Edited
						</span>
					)}
				</div>
			</div>

			<DeleteMessageDialog
				open={showDeleteDialog}
				handleDeleteMessage={handleDeleteMessage}
				onClose={() => setShowDeleteDialog(false)}
			/>
			{renderPhoneSheets()}
		</div>
	);
};

export default ChatBubble;
